import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useAuthStore } from './auth';
import api from '../services/api';

export const useTemplateStore = defineStore('templates', () => {
  // State
  const templates = ref([]);
  const currentTemplate = ref(null);
  const templateInstances = ref([]);
  const loading = ref(false);
  const error = ref(null);

  // Actions
  const fetchTemplates = async () => {
    loading.value = true;
    error.value = null;

    try {
      const data = await api.get('/v2/equipment-templates');
      // api.get() already unwraps the response, so data is the array
      templates.value = Array.isArray(data) ? data : (data.data || []);
      return templates.value;

    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const getTemplate = async (id, templateType = 'equipment') => {
    loading.value = true;
    error.value = null;

    try {
      // Choose the correct API endpoint based on template type
      const endpoint = templateType === 'device'
        ? `/v2/device-templates/${id}`
        : templateType === 'compound'
          ? `/v2/compound-equipment-templates/${id}`
          : `/v2/equipment-templates/${id}`;

      const data = await api.get(endpoint);

      // api.get() automatically unwraps responses, so data is already the template object
      const templateData = data;

      if (templateType === 'device') {
        // Normalize device template structure for TemplateEditor compatibility
        // Backend returns default_points, map to points for frontend use
        if (templateData && templateData.default_points) {
          templateData.points = templateData.default_points;
        }

        // Map device template fields to equipment template structure
        if (templateData) {
          templateData.brick_class = templateData.brick_class || templateData.device_type || '';
        }
      }

      currentTemplate.value = templateData;
      return templateData;

    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const createTemplate = async (templateData, templateType = 'equipment') => {
    loading.value = true;
    error.value = null;

    try {
      // Choose the correct API endpoint based on template type
      let endpoint;
      switch (templateType) {
        case 'device':
          endpoint = '/v2/device-templates';
          break;
        case 'controller':
          endpoint = '/v2/controller-templates';
          break;
        case 'compound':
          endpoint = '/v2/compound-equipment-templates';
          break;
        default:
          endpoint = '/v2/equipment-templates';
      }

      // Prepare the request data based on template type
      let requestData;
      if (templateType === 'device') {
        requestData = {
          name: templateData.name,
          description: templateData.description,
          brick_class: templateData.brick_class || templateData.device_type,
          device_part_id: templateData.device_part_id,
          metadata: templateData.metadata,
          default_points: templateData.points || templateData.default_points || [],
        };
      } else if (templateType === 'compound') {
        // Generate key from name if not provided
        const key = templateData.key || templateData.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
        requestData = {
          name: templateData.name,
          key: key,
          description: templateData.description,
          template_config: templateData.template_config || { components: [], relationships: [] },
          is_global: templateData.is_global ?? true,
          is_active: templateData.is_active ?? true,
        };
      } else {
        requestData = {
          name: templateData.name,
          description: templateData.description,
          brick_class: templateData.brick_class,
          default_devices: templateData.default_devices || [],
          default_points: templateData.default_points || [],
          properties: templateData.properties || null,
          functional_description: templateData.functional_description || null,
          programming: templateData.programming || null,
          hvac_air_data: templateData.hvac_air_data || null,
        };
      }

      const data = await api.post(endpoint, requestData);

      // Extract the created template from response
      const createdTemplate = data.data || data.template || data;

      // Add to templates list
      templates.value.unshift(createdTemplate);

      currentTemplate.value = createdTemplate;

      return createdTemplate;

    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const updateTemplate = async (id, templateData, templateType = 'equipment') => {
    loading.value = true;
    error.value = null;

    try {
      // Choose the correct API endpoint based on template type
      const endpoint = templateType === 'device'
        ? `/v2/device-templates/${id}`
        : templateType === 'compound'
          ? `/v2/compound-equipment-templates/${id}`
          : `/v2/equipment-templates/${id}`;

      // Prepare the request data - only send fields the backend expects
      let requestData;
      if (templateType === 'device') {
        // For device templates, send specific fields
        requestData = {
          name: templateData.name,
          description: templateData.description,
          brick_class: templateData.brick_class || templateData.device_type,
          metadata: templateData.metadata,
          default_points: templateData.points || templateData.default_points || [],
        };
      } else if (templateType === 'compound') {
        // For compound templates, send template_config with components and relationships
        requestData = {
          name: templateData.name,
          description: templateData.description,
          template_config: templateData.template_config || { components: [], relationships: [] },
          is_global: templateData.is_global ?? true,
          is_active: templateData.is_active ?? true,
        };
      } else {
        // For equipment templates, send all template fields
        requestData = {
          name: templateData.name,
          description: templateData.description,
          brick_class: templateData.brick_class,
          default_devices: templateData.default_devices || [],
          default_points: templateData.default_points || [],
          properties: templateData.properties || null,
          functional_description: templateData.functional_description || null,
          programming: templateData.programming || null,
          hvac_air_data: templateData.hvac_air_data || null,
        };
      }


      // Use api service instead of raw fetch
      const data = await api.put(endpoint, requestData);

      // api.put() already unwraps the response, so data is the response object
      let updatedTemplate, instancesSync, message;
      if (templateType === 'device') {
        // Device template API returns template object directly (after unwrapping)
        updatedTemplate = data.template || data;
        instancesSync = 0; // Device templates don't have instance sync info yet
        message = data.message;
      } else {
        // Equipment template API returns object with template and sync info (after unwrapping)
        updatedTemplate = data.template || data;
        instancesSync = data.instances_synchronized;
        message = data.message;
      }

      // Update in templates list
      const index = templates.value.findIndex((t) => t.id === id);
      if (index !== -1) {
        templates.value[index] = { ...templates.value[index], ...updatedTemplate };
      }

      currentTemplate.value = updatedTemplate;

      // Return enhanced response with sync info
      return {
        ...updatedTemplate,
        instances_synchronized: instancesSync,
        sync_message: message,
      };

    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const deleteTemplate = async (id, templateType = 'equipment') => {
    loading.value = true;
    error.value = null;

    try {
      // Choose the correct API endpoint based on template type
      const endpoint = templateType === 'device'
        ? `/v2/device-templates/${id}`
        : templateType === 'compound'
          ? `/v2/compound-equipment-templates/${id}`
          : `/v2/equipment-templates/${id}`;

      await api.delete(endpoint);

      // Remove from templates list
      templates.value = templates.value.filter((t) => t.id !== id);

      if (currentTemplate.value && currentTemplate.value.id === id) {
        currentTemplate.value = null;
      }

      return true;

    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const getTemplateInstances = async (id, siteId = null, templateType = 'equipment') => {
    loading.value = true;
    error.value = null;

    try {
      let url, data;

      if (templateType === 'device') {
        // Device template instances endpoint
        url = `/v2/device-templates/${id}/instances`;
        data = await api.get(url);

        // api.get() already unwraps, so data has the structure directly
        const instances = data.instances || [];
        templateInstances.value = instances;

        return {
          template: data.template,
          instances: instances,
          usage_count: data.usage_count || instances.length,
          instances_by_site: data.instances_by_site || {},
        };

      } else if (templateType === 'compound') {
        // Compound template instances endpoint
        url = `/v2/compound-equipment-templates/${id}/instances`;
        data = await api.get(url);

        // api.get() already unwraps, so data has the instances directly
        const instances = data.instances || [];
        templateInstances.value = instances;

        return {
          ...data,
          instances: instances,
          usage_count: instances.length,
        };

      } else {
        // Equipment template instances endpoint - use v2 endpoint
        url = `/v2/equipment-templates/${id}/instances`;
        data = await api.get(url);

        // api.get() already unwraps, so data has the instances directly
        const instances = data.instances || [];
        templateInstances.value = instances;

        return {
          ...data,
          instances: instances,
          usage_count: instances.length, // Show actual usage count across all sites
        };
      }

    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const getTemplateUsage = async (id) => {
    loading.value = true;
    error.value = null;

    try {
      const data = await api.get(`/equipment-templates/${id}/usage`);
      // api.get() already unwraps the response
      return data;

    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const createInstanceFromTemplate = async (templateId, instanceData) => {
    loading.value = true;
    error.value = null;

    try {
      const data = await api.post('/equipment-templates/create-instance', {
        template_id: templateId,
        ...instanceData,
      });
      // api.post() already unwraps the response
      return data;

    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const applyToAllInstances = async (templateId) => {
    // This would be a future enhancement for bulk updates
    // For now, we'll just refresh the template data
    return await getTemplate(templateId);
  };

  // Helper to find template by ID
  const findTemplateById = (id) => {
    return templates.value.find((t) => t.id === id);
  };

  // Helper to check if template is in use
  const isTemplateInUse = (id) => {
    const template = findTemplateById(id);
    return template && template.usage_count > 0;
  };

  // Clear error
  const clearError = () => {
    error.value = null;
  };

  // Reset store
  const resetStore = () => {
    templates.value = [];
    currentTemplate.value = null;
    templateInstances.value = [];
    loading.value = false;
    error.value = null;
  };

  return {
    // State
    templates,
    currentTemplate,
    templateInstances,
    loading,
    error,
    
    // Actions
    fetchTemplates,
    getTemplate,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    getTemplateInstances,
    getTemplateUsage,
    createInstanceFromTemplate,
    applyToAllInstances,
    
    // Helpers
    findTemplateById,
    isTemplateInUse,
    clearError,
    resetStore,
  };
});